import { useEffect, useState } from 'react';
import {
  Info,
  Palette,
  Type,
  Ruler,
  CornerDownRight,
  Sparkles,
  Zap,
  MousePointerClick,
  FormInput,
  ChevronDown,
  SquareCheck,
  CircleDot,
  ToggleLeft,
  Tag,
  LayoutGrid,
  Square,
  MessageSquare,
  Table as TableIcon,
  Layers,
  Bell,
  Navigation,
  ScrollText,
  Heading1,
  ArrowLeft,
  FileDown,
  Sliders,
  SplitSquareHorizontal,
  ListOrdered,
  Rows3,
  BarChart2,
} from 'lucide-react';
import { downloadDesignSystemMarkdown } from './branding-export';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import {
  ColorsSection,
  TypographySection,
  SpacingSection,
  RadiusSection,
  ShadowsSection,
  MotionSection,
  IconsSection,
} from './branding/sections/foundations';
import {
  CardSection,
  DividerSection,
  PageHeaderSection,
  BackLinkSection,
  SettingsSectionDemo,
} from './branding/sections/layout';
import {
  ButtonsSection,
  InputsSection,
  SelectSection,
  TextareaSection,
  CheckboxSection,
  RadioSection,
  ToggleSection,
  ToggleRowSection,
  SliderSection,
  CategoryAutoActionRowSection,
} from './branding/sections/forms';
import {
  ModalSection,
  TooltipSection,
  DropdownSection,
  NavigationSection,
  SegmentedSection,
} from './branding/sections/navigation';
import {
  BadgeSection,
  FeedbackSection,
  TableSection,
  ProgressBarSection,
} from './branding/sections/data';
import { SpecializedSection } from './branding/sections/specialized';
import { CopyNumber } from './branding/components/CopyNumber';
import { SectionNumberProvider } from './branding/components/Subsection';

/**
 * Strona /branding — living style guide dla EcomFlow.
 *
 * Renderuje wszystkie tokeny i komponenty z design systemu, żeby można
 * było weryfikować wizualnie co aktualnie jest w kodzie (tailwind.config.js
 * + components/ui/).
 *
 * Dostępna TYLKO dla SUPER_ADMIN (gated w App.tsx).
 *
 * Plan migracji: docs/branding-plan.md — każdy sprint wypełnia kolejną
 * sekcję tej strony.
 */

interface SectionDef {
  id: string;
  title: string;
  icon: typeof Palette;
  description: string;
  group: 'foundations' | 'forms' | 'layout' | 'feedback' | 'navigation' | 'data' | 'specialized';
}

const GROUP_LABEL: Record<SectionDef['group'], string> = {
  foundations: 'Fundamenty',
  layout:      'Layout',
  forms:       'Formularze',
  navigation:  'Nawigacja',
  feedback:    'Feedback',
  data:        'Dane',
  specialized: 'Specjalne',
};

const SECTIONS: SectionDef[] = [
  // Fundamenty
  { id: 'colors',      title: 'Kolory',              icon: Palette,           description: 'Surfaces, text, accent, status, border', group: 'foundations' },
  { id: 'typography',  title: 'Typografia',          icon: Type,              description: 'Rozmiary, wagi, line-heights, tracking', group: 'foundations' },
  { id: 'spacing',     title: 'Spacing',             icon: Ruler,             description: 'Skala 4/8/12/16/24/32 + field/card/section', group: 'foundations' },
  { id: 'radius',      title: 'Border radius',       icon: CornerDownRight,   description: 'Button, input, card, badge', group: 'foundations' },
  { id: 'shadows',     title: 'Shadows',             icon: Sparkles,          description: 'Glass, neon glow, elevation', group: 'foundations' },
  { id: 'motion',      title: 'Motion',              icon: Zap,               description: 'Durations, timing functions, animations', group: 'foundations' },
  { id: 'icons',       title: 'Ikony',               icon: Palette,           description: 'Lucide - rozmiary i konteksty użycia', group: 'foundations' },

  // Layout
  { id: 'pageheader',  title: 'Page header',         icon: Heading1,          description: 'Nagłówek strony z ikoną, accent word, actions', group: 'layout' },
  { id: 'backlink',    title: 'Back link',           icon: ArrowLeft,         description: 'Powrót do strony rodzica', group: 'layout' },
  { id: 'card',        title: 'Card',                icon: LayoutGrid,        description: 'Default / Interactive / Elevated + StatCard', group: 'layout' },
  { id: 'divider',     title: 'Divider',             icon: Square,            description: 'Horizontal / Vertical, z labelem i bez', group: 'layout' },
  { id: 'settings',    title: 'Settings section',    icon: Rows3,             description: 'Karta sekcji ustawień', group: 'layout' },

  // Formularze
  { id: 'buttons',     title: 'Buttony',             icon: MousePointerClick, description: 'Primary / Secondary / Ghost / Danger / Icon × sm/md/lg', group: 'forms' },
  { id: 'inputs',      title: 'Inputy',              icon: FormInput,         description: 'Text / Email / Number / Date / Password + stany', group: 'forms' },
  { id: 'select',      title: 'Select',              icon: ChevronDown,       description: 'Native select z custom chevronem', group: 'forms' },
  { id: 'textarea',    title: 'Textarea',            icon: ScrollText,        description: 'Z auto-resize i max-height', group: 'forms' },
  { id: 'checkbox',    title: 'Checkbox',            icon: SquareCheck,       description: 'Custom styling, klikalna etykieta', group: 'forms' },
  { id: 'radio',       title: 'Radio',               icon: CircleDot,         description: 'Radio + RadioGroup generic wrapper', group: 'forms' },
  { id: 'toggle',      title: 'Toggle / Switch',     icon: ToggleLeft,        description: 'iOS-style pill switch', group: 'forms' },
  { id: 'togglerow',   title: 'Toggle row',          icon: SplitSquareHorizontal, description: 'Wiersz label + description + toggle', group: 'forms' },
  { id: 'slider',      title: 'Slider',              icon: Sliders,           description: 'Numeric range + breakpoint variant', group: 'forms' },
  { id: 'categoryautoactionrow', title: 'Category auto-action row', icon: Tag, description: 'Badge + Priority input + Action select per kategoria', group: 'forms' },

  // Nawigacja
  { id: 'nav',         title: 'Nawigacja',           icon: Navigation,        description: 'Tabs, Breadcrumbs, Pagination', group: 'navigation' },
  { id: 'segmented',   title: 'Segmented tabs',      icon: ListOrdered,       description: 'Pill-style mini tabs dla switcherów', group: 'navigation' },
  { id: 'modal',       title: 'Modal',               icon: MessageSquare,     description: 'Dialog z backdropem', group: 'navigation' },
  { id: 'tooltip',     title: 'Tooltip',             icon: Info,              description: 'Hover tooltip z delay + positioning', group: 'navigation' },
  { id: 'dropdown',    title: 'Dropdown',            icon: ChevronDown,       description: 'Custom menu + Popover + ContextMenu', group: 'navigation' },

  // Status / dane
  { id: 'badge',       title: 'Badge',               icon: Tag,               description: 'Success / Warning / Danger / Info / Neutral / Accent', group: 'data' },
  { id: 'feedback',    title: 'Feedback',            icon: Bell,              description: 'Alert, Toast, Loader, Skeleton, EmptyState', group: 'feedback' },
  { id: 'table',       title: 'Tabele',              icon: TableIcon,         description: 'Table + DataList', group: 'data' },
  { id: 'progress',    title: 'Progress bar',        icon: BarChart2,         description: 'Poziomy pasek postępu z label + procent', group: 'data' },

  // Specjalne
  { id: 'specialized', title: 'Specjalne',           icon: Layers,            description: 'SearchInput, CopyButton, FileUpload, Accordion', group: 'specialized' },
];

/**
 * Treść design systemu - bez layout wrappera i headera, żeby można
 * osadzić w Admin Panel tabie. Standalone widok to `BrandingPage`.
 */
export function BrandingContent() {
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', `#${id}`);
    }
  };

  const grouped = SECTIONS.reduce<Record<string, SectionDef[]>>((acc, s) => {
    (acc[s.group] ||= []).push(s);
    return acc;
  }, {});
  const groupOrder: SectionDef['group'][] = ['foundations', 'layout', 'forms', 'navigation', 'data', 'feedback', 'specialized'];

  // Numeracja top-level: numer = pozycja w SECTIONS + 1 (1..N).
  // User klika "Skopiuj" → numer ląduje w clipboardzie i może go wkleić
  // przy feedbacku ("nie podoba mi się 15").
  const sectionNumber = (id: string): string => {
    const idx = SECTIONS.findIndex(s => s.id === id);
    return idx >= 0 ? String(idx + 1) : '?';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-6">
      {/* Sticky sidebar - lg+ only */}
      <aside className="hidden lg:block">
        <nav className="sticky top-4 max-h-[calc(100vh-6rem)] overflow-y-auto pr-2 -mr-2 space-y-4">
          {groupOrder.filter(g => grouped[g]?.length).map(group => (
            <div key={group}>
              <div className="text-[10px] uppercase tracking-wider text-text-disabled font-semibold mb-1.5 px-3">
                {GROUP_LABEL[group]}
              </div>
              <div className="space-y-0.5">
                {grouped[group].map(section => {
                  const Icon = section.icon;
                  const isActive = activeId === section.id;
                  return (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      onClick={(e) => handleNavClick(e, section.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                        isActive
                          ? 'bg-accent-primary/15 text-text-primary border border-accent-primary/30'
                          : 'text-text-muted hover:text-text-primary hover:bg-surface-hover/40 border border-transparent'
                      }`}
                    >
                      <span className={`shrink-0 w-5 text-right font-mono text-[10px] font-semibold ${isActive ? 'text-accent-primary' : 'text-text-disabled'}`}>
                        {sectionNumber(section.id)}.
                      </span>
                      <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-accent-primary' : 'text-text-disabled'}`} />
                      <span className="truncate">{section.title}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <div className="min-w-0">
      {/* Toolbar - theme toggle + export */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <p className="text-xs text-text-disabled">
          {SECTIONS.length} sekcji · pogrupowane po kategoriach
        </p>
        <div className="flex items-center gap-3">
          <ThemeToggle size="sm" />
          <button
            onClick={() => downloadDesignSystemMarkdown()}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-accent-primary/10 text-accent-primary border border-accent-primary/20 hover:bg-accent-primary/20 transition-colors text-xs font-medium"
          >
            <FileDown className="w-3.5 h-3.5" />
            Eksportuj design system (.md)
          </button>
        </div>
      </div>

      {/* Sekcje - zawartość. Nawigacja tylko przez sticky sidebar po lewej
          (numery 1-N) + numery w section headers. Grid cards usunięty — po
          wprowadzeniu numeracji był redundantny względem sidebara. */}
      <div className="space-y-12">
        {SECTIONS.map(section => {
          const Icon = section.icon;
          const filledMap: Record<string, JSX.Element | undefined> = {
            colors: <ColorsSection />,
            typography: <TypographySection />,
            spacing: <SpacingSection />,
            radius: <RadiusSection />,
            shadows: <ShadowsSection />,
            motion: <MotionSection />,
            icons: <IconsSection />,
            buttons: <ButtonsSection />,
            inputs:   <InputsSection />,
            select:   <SelectSection />,
            textarea: <TextareaSection />,
            checkbox: <CheckboxSection />,
            radio:    <RadioSection />,
            toggle:   <ToggleSection />,
            badge:    <BadgeSection />,
            card:     <CardSection />,
            divider:  <DividerSection />,
            modal:    <ModalSection />,
            tooltip:  <TooltipSection />,
            dropdown: <DropdownSection />,
            nav:      <NavigationSection />,
            feedback: <FeedbackSection />,
            table:    <TableSection />,
            progress: <ProgressBarSection />,
            specialized: <SpecializedSection />,
            pageheader:  <PageHeaderSection />,
            backlink:    <BackLinkSection />,
            settings:    <SettingsSectionDemo />,
            togglerow:   <ToggleRowSection />,
            slider:      <SliderSection />,
            categoryautoactionrow: <CategoryAutoActionRowSection />,
            segmented:   <SegmentedSection />,
          };
          const filledNode = filledMap[section.id];
          const n = sectionNumber(section.id);
          return (
            <section key={section.id} id={section.id} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-5 pb-3 border-b border-border-subtle">
                <Icon className="w-5 h-5 text-text-muted" />
                <h2 className="text-2xl font-bold text-text-primary">
                  <span className="font-mono text-accent-primary mr-2">{n}.</span>
                  {section.title}
                </h2>
                <CopyNumber number={n} />
              </div>
              <SectionNumberProvider number={n}>
                {filledNode}
              </SectionNumberProvider>
            </section>
          );
        })}
      </div>
      </div>
    </div>
  );
}

